import React, { useEffect, useRef } from 'react';
import { DialogContent, makeStyles } from 'housecall-ui';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';

import { trackEvent, trackPage, useAnalyticsHomeownerParams } from '../../hooks/useEventTracking';
import { useOrgData } from '../../hooks/useOrgData';
import {
  getReserveWithGoogleToken,
  LOCAL_STORAGE_RWG_TOKEN_KEY,
} from '../../resources/google/trackGoogleReserveConversion';
import { useOnlineBookingReducer } from '../../state/useOnlineBookingReducer';
import { STEP_HASH_MAP } from '../../Steps/consts';
import { analytics, initSegment } from '../../utils/external-libs/segment';
import { localStorage } from '../../utils/storage';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import { Spinner } from '../Spinner';

import { HomeOwnerFlowContent } from './HomeOwnerFlowContent';
import { SuccessView } from './SuccessView';

// After completely removing the online booking v1 we can use ListSkeleton instead of Spinner here
const LoadingSkeleton = () => {
  return <Spinner show />;
};

const useStyles = makeStyles((theme: HCPTheme) => ({
  content: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(3),
    paddingBottom: ({ hiddenButtons }: { hiddenButtons?: boolean }) =>
      hiddenButtons ? theme.spacing(8) : theme.spacing(13),
    height: '720px',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      maxHeight: 'none',
    },
  },
}));

export const HomeOwnerFlowContentWrapper = () => {
  const {
    state: { token, success, stepKey },
  } = useOnlineBookingReducer();
  const { isLoading, isFetched } = useOrgData({ token });
  const isSegmentInitialized = useRef(false);

  const selectedStep = STEP_HASH_MAP[stepKey];
  const { content } = useStyles({ hiddenButtons: selectedStep?.component.hiddenButtons });

  const params = useAnalyticsHomeownerParams();

  useEffect(() => {
    if (!params) {
      return;
    }

    if (!isSegmentInitialized.current) {
      initSegment();
      analytics.identify().then(() => {
        trackEvent('Online booking - opened', params);
        trackPage('Online Booking - Homeowner flow', {
          ...params,
          step: 1,
          step_name: 'categories',
        });
      });

      isSegmentInitialized.current = true;
    }
  }, [isSegmentInitialized, params]);

  useEffect(() => {
    const rwgToken = getReserveWithGoogleToken();

    if (rwgToken) {
      const monthInMilliseconds = 2592000000;
      const item = {
        value: rwgToken,
        expiry: Date.now() + monthInMilliseconds,
      };

      localStorage.setItem(LOCAL_STORAGE_RWG_TOKEN_KEY, JSON.stringify(item));
    }
  }, []);

  if (isLoading || !isFetched) {
    return (
      <DialogContent className={content}>
        <LoadingSkeleton />
      </DialogContent>
    );
  }

  if (!token) {
    return <DialogContent className={content}>Invalid organization identifier provided</DialogContent>;
  }

  return (
    <ErrorBoundary>
      <DialogContent className={content}>{success ? <SuccessView /> : <HomeOwnerFlowContent />}</DialogContent>
    </ErrorBoundary>
  );
};
